import React, { useState, useEffect } from "react";
import { Table, ProgressBar, Tabs, Tab } from "react-bootstrap";
import Hexapi from "../usefulcomps/HexAPI/Hexapi";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Dashboard.css";

// Currency & Date helpers
const formatCurrency = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(num);
};

const formatDate = (dateObj) => {
  if (!dateObj) return "-";
  const dateStr = typeof dateObj === "object" ? dateObj.date : dateObj;
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pipelineTab, setPipelineTab] = useState("segment");

  useEffect(() => {
    getUserDetails();
  }, []);

  const getUserDetails = async () => {
    const sessionId = localStorage.getItem("SessionID");
    if (!sessionId) {
      console.warn("No SessionID found in localStorage.");
      setLoading(false);
      return;
    }

    const obj = {
      query: `[dbo].[CRM_Proc_User_GetDetails] @SessionID='{0}'`,
      queryArr: [`${sessionId}`],
    };

    try {
      const resp = await Hexapi(obj);
      const agentID = resp[""]?.[0]?.agentid;
      if (agentID) {
        fetchDashboardData(agentID);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      setLoading(false);
    }
  };

  const fetchDashboardData = async (agentID) => {
    setLoading(true);
    const obj = {
      query: `[dbo].[CRM_Proc_Dashboard_GetData] @AgentID='{0}'`,
      queryArr: [`${agentID}`],
    };

    try {
      const resp = await Hexapi(obj);
      const parsedData = typeof resp === "string" ? JSON.parse(resp) : resp;
      setDashboardData(parsedData);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "75vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="w-100 py-5 text-center text-muted">
        <h5>No dashboard data available.</h5>
      </div>
    );
  }

  // Extract all datasets from API response
  const kpis = dashboardData.kpis?.[0] || {};
  const insights = dashboardData.insights || [];
  const funnel = dashboardData.funnel || [];
  const stageAging = dashboardData.stageaging || [];
  const pendingPayments = dashboardData.pendingpayments || [];
  const followups = dashboardData.followups || [];
  const topCustomers = dashboardData.topcustomers || [];
  const quotationRegister = dashboardData.quotationregister || [];
  const monthlyTrend = dashboardData.monthlytrend || [];
  const dailyLeads = dashboardData.dailyleads || [];
  const pipelineBySegment = dashboardData.pipelinebysegment || [];
  const pipelineByRating = dashboardData.pipelinebyrating || [];
  const pipelineByDivision = dashboardData.pipelinebydivision || [];
  const pipelineByState = dashboardData.pipelinebystate || [];

  const kpiItems = [
    {
      header: "Total Leads",
      value: kpis.totalleads ?? 0,
      icon: "fa-users",
      iconColor: "#4f46e5",
      bgTint: "#eef2ff",
    },
    {
      header: "Open Leads",
      value: kpis.openleads ?? 0,
      icon: "fa-folder-open",
      iconColor: "#2563eb",
      bgTint: "#eff6ff",
    },
    {
      header: "New Leads (Mo)",
      value: kpis.newleadsmonth ?? 0,
      icon: "fa-user-plus",
      iconColor: "#059669",
      bgTint: "#ecfdf5",
    },
    {
      header: "Overdue Follow-ups",
      value: kpis.overduefollowups ?? 0,
      icon: "fa-clock",
      iconColor: "#dc2626",
      bgTint: "#fef2f2",
      badgeClass: "text-danger",
    },
    {
      header: "Quotes (Mo)",
      value: kpis.quotationsmonth ?? 0,
      icon: "fa-file-invoice",
      iconColor: "#d97706",
      bgTint: "#fffbeb",
    },
    {
      header: "Collections (All-Time)",
      value: formatCurrency(kpis.collectionsalltime),
      icon: "fa-indian-rupee-sign",
      iconColor: "#0d9488",
      bgTint: "#f0fdfa",
    },
  ];

  return (
    <div className="dashboard-fluid py-3 px-3 px-xl-4">
      {/* 1. Metric KPI Cards */}
      <div className="row g-3 mb-4">
        {kpiItems.map((card, idx) => (
          <div className="col-12 col-sm-6 col-md-4 col-xl-2" key={idx}>
            <div className="metric-kpi-card">
              <div className="d-flex justify-content-between align-items-start">
                <span className="metric-header">{card.header}</span>
                <div
                  className="metric-icon-circle"
                  style={{
                    backgroundColor: card.bgTint,
                    color: card.iconColor,
                  }}
                >
                  <i className={`fa-solid ${card.icon}`}></i>
                </div>
              </div>
              <div className={`metric-value mt-2 ${card.badgeClass || ""}`}>
                {card.value}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 2. Actionable Insights */}
      {insights.length > 0 && (
        <div className="dashboard-panel-card mb-4">
          <div className="card-header d-flex align-items-center">
            <i className="fa-solid fa-bolt-lightning text-warning me-2 fs-6"></i>
            <span>Actionable Insights</span>
          </div>
          <div className="card-body p-3 p-xl-4">
            <div className="row g-3">
              {insights.map((item, idx) => {
                const severityClass = `severity-${item.severity || "info"}`;
                const valColor =
                  item.severity === "danger"
                    ? "text-danger"
                    : item.severity === "warning"
                      ? "text-warning"
                      : item.severity === "success"
                        ? "text-success"
                        : "text-primary";

                return (
                  <div className="col-12 col-md-6 col-xl-4" key={idx}>
                    <div className={`insight-card ${severityClass}`}>
                      <div>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="insight-category-tag">
                            {item.category}
                          </span>
                          <span className={`insight-metric-val ${valColor}`}>
                            {item.value}
                          </span>
                        </div>
                        <div className="insight-title">{item.title}</div>
                      </div>
                      <p className="insight-msg mt-2">{item.message}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 3. Conversion Funnel & Stage Aging */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-xl-5">
          <div className="dashboard-panel-card h-100">
            <div className="card-header">
              <i className="fa-solid fa-filter text-primary me-2"></i>Lead
              Conversion Funnel
            </div>
            <div className="card-body p-3 p-xl-4">
              {funnel.map((step) => {
                const pct = parseFloat(step.pctofleads) || 0;
                return (
                  <div key={step.sortorder} className="mb-3">
                    <div className="d-flex justify-content-between mb-1 small fw-semibold">
                      <span>{step.stage}</span>
                      <span>
                        {step.leads} Leads ({step.pctofleads}%)
                      </span>
                    </div>
                    <ProgressBar
                      now={pct}
                      variant="primary"
                      style={{ height: "8px" }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-7">
          <div className="dashboard-panel-card h-100">
            <div className="card-header">
              <i className="fa-solid fa-hourglass-half text-secondary me-2"></i>
              Stage Aging Breakdown
            </div>
            <div className="card-body p-0">
              <Table responsive className="dashboard-table mb-0">
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th className="text-center">Active Leads</th>
                    <th className="text-center">Avg Days</th>
                    <th className="text-center">Max Days</th>
                    <th className="text-center">Stuck Leads</th>
                  </tr>
                </thead>
                <tbody>
                  {stageAging.map((row, idx) => (
                    <tr key={idx}>
                      <td className="fw-semibold">{row.stage}</td>
                      <td className="text-center">{row.leads}</td>
                      <td className="text-center">{row.avgdaysinstage} d</td>
                      <td className="text-center">{row.maxdaysinstage} d</td>
                      <td className="text-center text-danger fw-bold">
                        {row.stuckleads}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Pending Payments (Critical Alert Queue) */}
      {pendingPayments.length > 0 && (
        <div className="dashboard-panel-card border-danger mb-4">
          <div className="card-header bg-danger text-white py-2">
            <i className="fa-solid fa-circle-exclamation me-2"></i>Pending
            Proforma Invoices (Payment Due)
          </div>
          <div className="card-body p-0">
            <Table responsive className="dashboard-table mb-0">
              <thead>
                <tr>
                  <th>Lead ID</th>
                  <th>Customer</th>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>PI Raised On</th>
                  <th>Days Waiting</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingPayments.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.leadid}</td>
                    <td className="fw-semibold">{row.name}</td>
                    <td>{row.companyname}</td>
                    <td>{row.contactnumber}</td>
                    <td>{formatDate(row.piraisedon)}</td>
                    <td>
                      <span className="badge bg-danger">
                        {row.dayswaiting} Days
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-warning text-dark">
                        Payment Due
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {/* 5. Overdue Follow-ups & Top Customers */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-xl-7">
          <div className="dashboard-panel-card h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span>
                <i className="fa-solid fa-clock-rotate-left text-danger me-2"></i>
                Overdue Follow-ups
              </span>
              <span className="badge bg-danger rounded-pill">
                {followups.length} Overdue
              </span>
            </div>
            <div
              className="card-body p-0"
              style={{ maxHeight: "400px", overflowY: "auto" }}
            >
              <Table responsive className="dashboard-table mb-0">
                <thead className="sticky-top">
                  <tr>
                    <th>Lead</th>
                    <th>Company</th>
                    <th>Rating</th>
                    <th>Due Date</th>
                    <th>Days Overdue</th>
                  </tr>
                </thead>
                <tbody>
                  {followups.slice(0, 15).map((lead) => (
                    <tr key={lead.leadid}>
                      <td className="fw-semibold">{lead.name}</td>
                      <td>{lead.companyname}</td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {lead.rating || "N/A"}
                        </span>
                      </td>
                      <td>{formatDate(lead.nextfollowup)}</td>
                      <td>
                        <span className="badge bg-danger">
                          {lead.daysoverdue}d
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-5">
          <div className="dashboard-panel-card h-100">
            <div className="card-header">
              <i className="fa-solid fa-trophy text-warning me-2"></i>Top
              Customers by Revenue
            </div>
            <div className="card-body p-0">
              <Table responsive className="dashboard-table mb-0">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Revenue</th>
                    <th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {topCustomers.map((cust, idx) => (
                    <tr key={idx}>
                      <td>
                        <div className="fw-semibold">{cust.companyname}</div>
                        <div className="text-muted small">{cust.name}</div>
                      </td>
                      <td className="fw-bold text-success">
                        {formatCurrency(cust.revenue)}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <ProgressBar
                            now={parseFloat(cust.revenuesharepct) || 0}
                            style={{ width: "65px", height: "6px" }}
                          />
                          <span>{cust.revenuesharepct}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      {/* 6. Multi-Dimensional Pipeline Breakdown (Tabs) */}
      <div className="dashboard-panel-card mb-4">
        <div className="card-header pb-0 border-bottom-0">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <span className="mb-2">
              <i className="fa-solid fa-chart-pie text-info me-2"></i>Pipeline
              Segmentations
            </span>
            <Tabs
              activeKey={pipelineTab}
              onSelect={(k) => setPipelineTab(k)}
              className="border-bottom-0 mb-2"
            >
              <Tab eventKey="segment" title="By Segment" />
              <Tab eventKey="rating" title="By DG Rating" />
              <Tab eventKey="division" title="By Division" />
              <Tab eventKey="state" title="By State" />
            </Tabs>
          </div>
        </div>
        <div className="card-body p-0 border-top">
          <div style={{ maxHeight: "380px", overflowY: "auto" }}>
            {pipelineTab === "segment" && (
              <Table responsive className="dashboard-table mb-0">
                <thead className="sticky-top">
                  <tr>
                    <th>Segment</th>
                    <th className="text-center">Total Leads</th>
                    <th className="text-center">Open</th>
                    <th className="text-center">Quoted</th>
                    <th className="text-center">Won</th>
                    <th>Collected</th>
                    <th>Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {pipelineBySegment.map((row, idx) => (
                    <tr key={idx}>
                      <td className="fw-semibold">{row.natureofbusiness}</td>
                      <td className="text-center">{row.totalleads}</td>
                      <td className="text-center">{row.openleads}</td>
                      <td className="text-center">{row.quotedleads}</td>
                      <td className="text-center">{row.wonleads}</td>
                      <td className="fw-semibold">
                        {formatCurrency(row.collected)}
                      </td>
                      <td>{row.winratepct ? `${row.winratepct}%` : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {pipelineTab === "rating" && (
              <Table responsive className="dashboard-table mb-0">
                <thead className="sticky-top">
                  <tr>
                    <th>DG Rating</th>
                    <th className="text-center">Total Leads</th>
                    <th className="text-center">Open</th>
                    <th className="text-center">Quoted</th>
                    <th className="text-center">Won</th>
                    <th>Collected</th>
                    <th>Lead to Won</th>
                  </tr>
                </thead>
                <tbody>
                  {pipelineByRating.slice(0, 15).map((row, idx) => (
                    <tr key={idx}>
                      <td className="fw-semibold">
                        {row.rating || "Unspecified"}
                      </td>
                      <td className="text-center">{row.totalleads}</td>
                      <td className="text-center">{row.openleads}</td>
                      <td className="text-center">{row.quotedleads}</td>
                      <td className="text-center">{row.wonleads}</td>
                      <td className="fw-semibold">
                        {formatCurrency(row.collected)}
                      </td>
                      <td>{row.leadtowonpct}%</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {pipelineTab === "division" && (
              <Table responsive className="dashboard-table mb-0">
                <thead className="sticky-top">
                  <tr>
                    <th>Division</th>
                    <th className="text-center">Total Leads</th>
                    <th className="text-center">Open</th>
                    <th className="text-center">Won</th>
                    <th>Collected</th>
                    <th>Share of Leads</th>
                  </tr>
                </thead>
                <tbody>
                  {pipelineByDivision.map((row, idx) => (
                    <tr key={idx}>
                      <td className="fw-semibold">{row.typeofenquiry}</td>
                      <td className="text-center">{row.totalleads}</td>
                      <td className="text-center">{row.openleads}</td>
                      <td className="text-center">{row.wonleads}</td>
                      <td className="fw-semibold">
                        {formatCurrency(row.collected)}
                      </td>
                      <td>{row.shareofleadspct}%</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {pipelineTab === "state" && (
              <Table responsive className="dashboard-table mb-0">
                <thead className="sticky-top">
                  <tr>
                    <th>State</th>
                    <th className="text-center">Total Leads</th>
                    <th className="text-center">Quoted</th>
                    <th className="text-center">Won</th>
                    <th>Collected</th>
                    <th>Lead to Won</th>
                  </tr>
                </thead>
                <tbody>
                  {pipelineByState.map((row, idx) => (
                    <tr key={idx}>
                      <td className="fw-semibold">{row.state}</td>
                      <td className="text-center">{row.totalleads}</td>
                      <td className="text-center">{row.quotedleads}</td>
                      <td className="text-center">{row.wonleads}</td>
                      <td className="fw-semibold">
                        {formatCurrency(row.collected)}
                      </td>
                      <td>{row.leadtowonpct}%</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      </div>

      {/* 7. Quotation Register */}
      <div className="dashboard-panel-card mb-4">
        <div className="card-header">
          <i className="fa-solid fa-file-invoice text-primary me-2"></i>
          Quotation Register
        </div>
        <div className="card-body p-0">
          <div style={{ maxHeight: "380px", overflowY: "auto" }}>
            <Table responsive className="dashboard-table mb-0">
              <thead className="sticky-top">
                <tr>
                  <th>Ref No</th>
                  <th>Client / Company</th>
                  <th>Rating</th>
                  <th>Quoted Value</th>
                  <th>Discount</th>
                  <th>Quoted Date</th>
                  <th>Expiry</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {quotationRegister.slice(0, 15).map((quote) => {
                  const statusBadge =
                    quote.status === "Open"
                      ? "bg-primary"
                      : quote.status === "Expired"
                        ? "bg-danger"
                        : quote.status === "Paid"
                          ? "bg-success"
                          : "bg-warning text-dark";

                  return (
                    <tr key={quote.quoterecid}>
                      <td className="fw-semibold">{quote.refno}</td>
                      <td>
                        <div className="fw-semibold">{quote.companyname}</div>
                        <div className="text-muted small">{quote.name}</div>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {quote.rating}
                        </span>
                      </td>
                      <td className="fw-bold">
                        {formatCurrency(quote.quotation)}
                      </td>
                      <td>{quote.discount}%</td>
                      <td>{formatDate(quote.quotedon)}</td>
                      <td>{formatDate(quote.expirydate)}</td>
                      <td>
                        <span className={`badge ${statusBadge}`}>
                          {quote.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      {/* 8. Trends & Daily Activity Feed */}
      <div className="row g-4 mb-3">
        <div className="col-12 col-xl-6">
          <div className="dashboard-panel-card h-100">
            <div className="card-header">
              <i className="fa-solid fa-arrow-trend-up text-success me-2"></i>
              Monthly Performance Trend
            </div>
            <div className="card-body p-0">
              <Table responsive className="dashboard-table mb-0">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="text-center">New Leads</th>
                    <th className="text-center">Quotes</th>
                    <th className="text-center">PIs</th>
                    <th className="text-center">Orders Won</th>
                    <th>Collections</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyTrend.map((row, idx) => (
                    <tr key={idx}>
                      <td className="fw-semibold">{row.monthlabel}</td>
                      <td className="text-center">{row.newleads}</td>
                      <td className="text-center">{row.quotations}</td>
                      <td className="text-center">{row.pis}</td>
                      <td className="text-center">{row.orderswon}</td>
                      <td className="fw-semibold text-success">
                        {formatCurrency(row.collections)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-6">
          <div className="dashboard-panel-card h-100">
            <div className="card-header">
              <i className="fa-solid fa-calendar-day text-info me-2"></i>14-Day
              Activity Feed
            </div>
            <div
              className="card-body p-0"
              style={{ maxHeight: "300px", overflowY: "auto" }}
            >
              <Table responsive className="dashboard-table mb-0">
                <thead className="sticky-top">
                  <tr>
                    <th>Date</th>
                    <th className="text-center">New Leads</th>
                    <th className="text-center">Quotations</th>
                    <th>Collections</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyLeads.map((day, idx) => (
                    <tr key={idx}>
                      <td>{formatDate(day.daydate)}</td>
                      <td className="text-center fw-semibold">
                        {day.newleads}
                      </td>
                      <td className="text-center">{day.quotations}</td>
                      <td>{formatCurrency(day.collections)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
